import React, { useState, useEffect, useMemo } from "react";
import EncaissementService from "../../Services/Encaissement";
import DecaissementService from "../../Services/Decaissement";
import ExcelTable from "../../Components/Tables/VaultTable";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Card,
  CardContent,
  Pagination,
  Grid,
  useTheme,
  alpha,
  Paper,
  Avatar,
  Chip,
  Fade,
  Zoom,
  Grow,
  Tooltip,
  Badge,
  Divider,
  IconButton,
  InputAdornment
} from "@mui/material";
import {
  AccountBalance,
  TrendingUp,
  TrendingDown,
  DateRange,
  FilterList,
  Search,
  Refresh,
  Receipt,
  CalendarToday,
  MonetizationOn,
  AttachMoney,
  MoneyOff,
  Store,
  TableChart,
  ArrowDownward,
  ArrowUpward,
  Clear,
  Download,
  Print,
  Visibility
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const CaisseTransactions = () => {
  const theme = useTheme();
  const [encaissements, setEncaissements] = useState([]);
  const [decaissements, setDecaissements] = useState([]);
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [hoveredCard, setHoveredCard] = useState(null);
  const restaurantId = localStorage.getItem("restaurantId");
  const rowsPerPage = 30;

  useEffect(() => {
    const fetchData = async () => {
      if (!restaurantId) return;
      
      try {
        const fetchedEncaissements = await EncaissementService.getAllEncaissements();
        const fetchedDecaissements = await DecaissementService.getAllDecaissements();

        const filteredEncaissements = fetchedEncaissements.filter(
          (row) => row.restaurantId === restaurantId
        );
        const filteredDecaissements = fetchedDecaissements.filter(
          (row) => row.restaurantId === restaurantId
        );

        setEncaissements(filteredEncaissements);
        setDecaissements(filteredDecaissements);
      } catch (error) {
        console.error("Error fetching data:", error.message);
      }
    };

    fetchData();
  }, [restaurantId]);

  const combinedData = useMemo(() => {
    if (!restaurantId) return [];
    return [
      ...encaissements.map((row) => ({ ...row, category: "ENCAISSEMENT" })),
      ...decaissements.map((row) => ({ ...row, category: "DECAISSEMENT" })),
    ];
  }, [encaissements, decaissements, restaurantId]);

  // Statistics calculations
  const totalEncaissements = useMemo(() => {
    return combinedData
      .filter(row => row.category === "ENCAISSEMENT")
      .reduce((sum, row) => sum + parseFloat(row.value), 0);
  }, [combinedData]);

  const totalDecaissements = useMemo(() => {
    return combinedData
      .filter(row => row.category === "DECAISSEMENT")
      .reduce((sum, row) => sum + parseFloat(row.value), 0);
  }, [combinedData]);

  const balance = totalEncaissements - totalDecaissements;

  const currentMonthData = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    return combinedData.filter(row => {
      const rowDate = new Date(row.date);
      return rowDate.getMonth() === currentMonth && rowDate.getFullYear() === currentYear;
    });
  }, [combinedData]);

  const currentMonthEncaissements = useMemo(() => {
    return currentMonthData
      .filter(row => row.category === "ENCAISSEMENT")
      .reduce((sum, row) => sum + parseFloat(row.value), 0);
  }, [currentMonthData]);

  const currentMonthDecaissements = useMemo(() => {
    return currentMonthData
      .filter(row => row.category === "DECAISSEMENT")
      .reduce((sum, row) => sum + parseFloat(row.value), 0);
  }, [currentMonthData]);

  useEffect(() => {
    if (!restaurantId) return;

    const filtered = combinedData.filter((row) => {
      const transactionDate = new Date(row.date);
      const startDate = dateDebut ? new Date(dateDebut) : null;
      const endDate = dateFin ? new Date(dateFin) : null;

      if (startDate) startDate.setUTCHours(0, 0, 0, 0);
      if (endDate) endDate.setUTCHours(23, 59, 59, 999);

      const matchesDateRange =
        (!startDate || transactionDate >= startDate) &&
        (!endDate || transactionDate <= endDate);
      
      const matchesType = filterType ? row.category === filterType : true;
      
      const matchesSearch = searchTerm
        ? row.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          row.value?.toString().includes(searchTerm) ||
          row.date?.includes(searchTerm)
        : true;

      return matchesDateRange && matchesType && matchesSearch;
    });

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [dateDebut, dateFin, filterType, searchTerm, combinedData, restaurantId]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, rowsPerPage]);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const clearFilters = () => {
    setDateDebut("");
    setDateFin("");
    setFilterType("");
    setSearchTerm("");
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'TND',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num || 0);
  };

  const formatCompact = (num) => {
    return new Intl.NumberFormat('fr-FR', {
      notation: 'compact',
      compactDisplay: 'short',
      maximumFractionDigits: 1
    }).format(num || 0);
  };

  if (!restaurantId) {
    return (
      <Box sx={{ p: 4, minHeight: '100vh', background: '#F5F7FA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Zoom in={true}>
          <Paper
            elevation={3}
            sx={{
              p: 6,
              borderRadius: 4,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)',
              border: '1px solid #E0E0E0',
              maxWidth: 500
            }}
          >
            <Store sx={{ fontSize: 80, color: '#9AA6B2', mb: 2 }} />
            <Typography variant="h4" sx={{ color: '#1A2B3C', fontWeight: 700, mb: 1 }}>
              Aucun restaurant sélectionné
            </Typography>
            <Typography variant="body1" sx={{ color: '#5F6B7A' }}>
              Veuillez sélectionner un restaurant pour voir les transactions
            </Typography>
          </Paper>
        </Zoom>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 4, 
      background: '#F5F7FA',
      minHeight: '100vh',
      position: 'relative',
    }}>
      {/* Header with Stats Cards */}
      <motion.div
        initial="initial"
        animate="animate"
        variants={staggerContainer}
      >
        {/* Main Header */}
        <motion.div variants={fadeInUp}>
          <Paper
            elevation={0}
            sx={{
              mb: 4,
              background: 'linear-gradient(135deg, #1E2A3A 0%, #2C3E50 100%)',
              borderRadius: 4,
              border: '1px solid rgba(255,255,255,0.1)',
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0) 100%)',
                transition: 'left 0.6s',
              },
              '&:hover::before': {
                left: '100%'
              }
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Zoom in={true} style={{ transitionDelay: '200ms' }}>
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                        boxShadow: '0 10px 20px rgba(255,215,0,0.3)',
                      }}
                    >
                      <AccountBalance sx={{ fontSize: 48, color: '#1E2A3A' }} />
                    </Avatar>
                  </Zoom>
                  <Box>
                    <Typography 
                      variant="h3" 
                      sx={{ 
                        fontWeight: 800,
                        background: 'linear-gradient(45deg, #FFFFFF 30%, #FFD700 90%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 1,
                      }}
                    >
                      Caisse Transactions
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#B0BEC5', fontWeight: 400 }}>
                      Suivi complet des entrées et sorties d'argent
                    </Typography>
                  </Box>
                </Box>
                <Badge
                  badgeContent={filteredData.length}
                  color="primary"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '1rem',
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                      color: '#1E2A3A',
                      fontWeight: 700
                    }
                  }}
                >
                  <Receipt sx={{ fontSize: 60, color: 'rgba(255,255,255,0.3)' }} />
                </Badge>
              </Box>
            </CardContent>
          </Paper>
        </motion.div>

        {/* Stats Grid */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Balance Card */}
          <Grid item xs={12} md={4}>
            <motion.div variants={fadeInUp}>
              <Card
                onMouseEnter={() => setHoveredCard('balance')}
                onMouseLeave={() => setHoveredCard(null)}
                sx={{
                  background: hoveredCard === 'balance' 
                    ? 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)'
                    : '#FFFFFF',
                  borderRadius: 3,
                  border: '1px solid #E0E0E0',
                  transition: 'all 0.3s ease',
                  transform: hoveredCard === 'balance' ? 'translateY(-5px)' : 'none',
                  boxShadow: hoveredCard === 'balance' ? '0 20px 30px rgba(0,0,0,0.1)' : '0 4px 12px rgba(0,0,0,0.05)',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#5F6B7A', textTransform: 'uppercase', letterSpacing: 1 }}>
                        Solde Net
                      </Typography>
                      <Typography variant="h4" sx={{ 
                        color: balance >= 0 ? '#2E7D32' : '#D32F2F', 
                        fontWeight: 700, 
                        mt: 1 
                      }}>
                        {formatNumber(balance)}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ArrowUpward sx={{ color: '#2E7D32', fontSize: 16 }} />
                          <Typography variant="caption" sx={{ color: '#5F6B7A' }}>
                            {formatCompact(totalEncaissements)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ArrowDownward sx={{ color: '#D32F2F', fontSize: 16 }} />
                          <Typography variant="caption" sx={{ color: '#5F6B7A' }}>
                            {formatCompact(totalDecaissements)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: balance >= 0 ? '#E8F5E9' : '#FFEBEE',
                        color: balance >= 0 ? '#2E7D32' : '#D32F2F',
                      }}
                    >
                      {balance >= 0 ? <TrendingUp /> : <TrendingDown />}
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Total Encaissements Card */}
          <Grid item xs={12} md={4}>
            <motion.div variants={fadeInUp}>
              <Card
                onMouseEnter={() => setHoveredCard('encaissement')}
                onMouseLeave={() => setHoveredCard(null)}
                sx={{
                  background: hoveredCard === 'encaissement' 
                    ? 'linear-gradient(135deg, #E8F5E9 0%, #FFFFFF 100%)'
                    : '#FFFFFF',
                  borderRadius: 3,
                  border: '1px solid #E0E0E0',
                  transition: 'all 0.3s ease',
                  transform: hoveredCard === 'encaissement' ? 'translateY(-5px)' : 'none',
                  boxShadow: hoveredCard === 'encaissement' ? '0 20px 30px rgba(46,125,50,0.1)' : '0 4px 12px rgba(0,0,0,0.05)',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#5F6B7A', textTransform: 'uppercase', letterSpacing: 1 }}>
                        Total Encaissements
                      </Typography>
                      <Typography variant="h5" sx={{ color: '#2E7D32', fontWeight: 700, mt: 1 }}>
                        {formatNumber(totalEncaissements)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#5F6B7A', display: 'block', mt: 1 }}>
                        {encaissements.length} transactions
                      </Typography>
                    </Box>
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: '#E8F5E9',
                        color: '#2E7D32',
                      }}
                    >
                      <AttachMoney />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Total Decaissements Card */}
          <Grid item xs={12} md={4}>
            <motion.div variants={fadeInUp}>
              <Card
                onMouseEnter={() => setHoveredCard('decaissement')}
                onMouseLeave={() => setHoveredCard(null)}
                sx={{
                  background: hoveredCard === 'decaissement' 
                    ? 'linear-gradient(135deg, #FFEBEE 0%, #FFFFFF 100%)'
                    : '#FFFFFF',
                  borderRadius: 3,
                  border: '1px solid #E0E0E0',
                  transition: 'all 0.3s ease',
                  transform: hoveredCard === 'decaissement' ? 'translateY(-5px)' : 'none',
                  boxShadow: hoveredCard === 'decaissement' ? '0 20px 30px rgba(211,47,47,0.1)' : '0 4px 12px rgba(0,0,0,0.05)',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#5F6B7A', textTransform: 'uppercase', letterSpacing: 1 }}>
                        Total Décaissements
                      </Typography>
                      <Typography variant="h5" sx={{ color: '#D32F2F', fontWeight: 700, mt: 1 }}>
                        {formatNumber(totalDecaissements)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#5F6B7A', display: 'block', mt: 1 }}>
                        {decaissements.length} transactions
                      </Typography>
                    </Box>
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: '#FFEBEE',
                        color: '#D32F2F',
                      }}
                    >
                      <MoneyOff />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Current Month Stats */}
          <Grid item xs={12} md={6}>
            <motion.div variants={fadeInUp}>
              <Card sx={{ 
                background: '#FFFFFF',
                borderRadius: 3,
                border: '1px solid #E0E0E0',
              }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ color: '#1A2B3C', fontWeight: 600, mb: 2 }}>
                    <CalendarToday sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle', color: '#9AA6B2' }} />
                    Ce mois-ci
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Chip
                        icon={<TrendingUp />}
                        label="Encaissements"
                        size="small"
                        sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', mb: 1 }}
                      />
                      <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 700 }}>
                        {formatNumber(currentMonthEncaissements)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#5F6B7A' }}>
                        {currentMonthData.filter(r => r.category === "ENCAISSEMENT").length} transactions
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                    <Box sx={{ textAlign: 'center' }}>
                      <Chip
                        icon={<TrendingDown />}
                        label="Décaissements"
                        size="small"
                        sx={{ bgcolor: '#FFEBEE', color: '#D32F2F', mb: 1 }}
                      />
                      <Typography variant="h6" sx={{ color: '#D32F2F', fontWeight: 700 }}>
                        {formatNumber(currentMonthDecaissements)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#5F6B7A' }}>
                        {currentMonthData.filter(r => r.category === "DECAISSEMENT").length} transactions
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Quick Stats */}
          <Grid item xs={12} md={6}>
            <motion.div variants={fadeInUp}>
              <Card sx={{ 
                background: '#FFFFFF',
                borderRadius: 3,
                border: '1px solid #E0E0E0',
              }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ color: '#1A2B3C', fontWeight: 600, mb: 2 }}>
                    <Receipt sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle', color: '#9AA6B2' }} />
                    Aperçu rapide
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ p: 1.5, bgcolor: '#F5F7FA', borderRadius: 2 }}>
                        <Typography variant="caption" sx={{ color: '#5F6B7A', display: 'block' }}>
                          Total transactions
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#1A2B3C', fontWeight: 700 }}>
                          {combinedData.length}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ p: 1.5, bgcolor: '#F5F7FA', borderRadius: 2 }}>
                        <Typography variant="caption" sx={{ color: '#5F6B7A', display: 'block' }}>
                          Types distincts
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#1A2B3C', fontWeight: 700 }}>
                          {new Set(combinedData.map(d => d.type)).size}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>

        {/* Filters Section */}
        <motion.div variants={fadeInUp}>
          <Card sx={{ 
            mb: 4, 
            borderRadius: 3,
            border: '1px solid #E0E0E0',
            overflow: 'hidden'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: '#9AA6B2' }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearchTerm('')}>
                            <Clear fontSize="small" sx={{ color: '#9AA6B2' }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                      sx: {
                        backgroundColor: '#F5F7FA',
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E0E0E0',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976D2',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976D2',
                        },
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={2.5}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Date début"
                    value={dateDebut}
                    onChange={(e) => setDateDebut(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarToday sx={{ color: '#9AA6B2', fontSize: 18 }} />
                        </InputAdornment>
                      ),
                      sx: {
                        backgroundColor: '#F5F7FA',
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E0E0E0',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976D2',
                        },
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={2.5}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Date fin"
                    value={dateFin}
                    onChange={(e) => setDateFin(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarToday sx={{ color: '#9AA6B2', fontSize: 18 }} />
                        </InputAdornment>
                      ),
                      sx: {
                        backgroundColor: '#F5F7FA',
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E0E0E0',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976D2',
                        },
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={2.5}>
                  <FormControl fullWidth>
                    <Select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      displayEmpty
                      sx={{
                        backgroundColor: '#F5F7FA',
                        borderRadius: 2,
                        height: 56,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E0E0E0',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976D2',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976D2',
                        },
                      }}
                    >
                      <MenuItem value="">Tous les types</MenuItem>
                      <MenuItem value="ENCAISSEMENT">Encaissements</MenuItem>
                      <MenuItem value="DECAISSEMENT">Décaissements</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={1.5}>
                  <Tooltip title="Réinitialiser les filtres">
                    <IconButton
                      onClick={clearFilters}
                      sx={{
                        width: 56,
                        height: 56,
                        backgroundColor: '#F5F7FA',
                        borderRadius: 2,
                        border: '1px solid #E0E0E0',
                        '&:hover': {
                          borderColor: '#1976D2',
                          backgroundColor: '#E3F2FD',
                        },
                      }}
                    >
                      <Refresh sx={{ color: '#5F6B7A' }} />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>

              {/* Active Filters Display */}
              {(dateDebut || dateFin || filterType || searchTerm) && (
                <Fade in={true}>
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {searchTerm && (
                      <Chip
                        label={`Recherche: ${searchTerm}`}
                        onDelete={() => setSearchTerm('')}
                        size="small"
                        sx={{ backgroundColor: '#E3F2FD', color: '#1976D2' }}
                      />
                    )}
                    {dateDebut && (
                      <Chip
                        label={`Du: ${dateDebut}`}
                        onDelete={() => setDateDebut('')}
                        size="small"
                        sx={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }}
                      />
                    )}
                    {dateFin && (
                      <Chip
                        label={`Au: ${dateFin}`}
                        onDelete={() => setDateFin('')}
                        size="small"
                        sx={{ backgroundColor: '#FFF3E0', color: '#ED6C02' }}
                      />
                    )}
                    {filterType && (
                      <Chip
                        label={filterType === "ENCAISSEMENT" ? "Encaissements" : "Décaissements"}
                        onDelete={() => setFilterType('')}
                        size="small"
                        sx={{ 
                          backgroundColor: filterType === "ENCAISSEMENT" ? '#E8F5E9' : '#FFEBEE',
                          color: filterType === "ENCAISSEMENT" ? '#2E7D32' : '#D32F2F'
                        }}
                      />
                    )}
                  </Box>
                </Fade>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Table Section */}
        <motion.div variants={fadeInUp}>
          <Card sx={{ 
            borderRadius: 3,
            border: '1px solid #E0E0E0',
            overflow: 'hidden'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TableChart sx={{ color: '#1976D2' }} />
                  <Typography variant="h6" sx={{ color: '#1A2B3C', fontWeight: 600 }}>
                    Liste des transactions
                  </Typography>
                  <Chip
                    label={`${filteredData.length} résultats`}
                    size="small"
                    sx={{ ml: 1, backgroundColor: '#E3F2FD', color: '#1976D2' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Télécharger">
                    <IconButton sx={{ color: '#5F6B7A', '&:hover': { color: '#1976D2' } }}>
                      <Download />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Imprimer">
                    <IconButton sx={{ color: '#5F6B7A', '&:hover': { color: '#1976D2' } }}>
                      <Print />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <div style={{ overflowX: "auto" }}>
                <ExcelTable data={paginatedData} />
              </div>

              {filteredData.length === 0 && (
                <Fade in={true}>
                  <Box sx={{ py: 8, textAlign: 'center' }}>
                    <Receipt sx={{ fontSize: 60, color: '#B0BEC5', mb: 2 }} />
                    <Typography variant="h6" sx={{ color: '#5F6B7A' }}>
                      Aucune transaction trouvée
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#9AA6B2' }}>
                      Essayez de modifier vos filtres de recherche
                    </Typography>
                  </Box>
                </Fade>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Pagination */}
        {filteredData.length > 0 && (
          <motion.div variants={fadeInUp}>
            <Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
              <Paper elevation={0} sx={{ p: 1, backgroundColor: '#FFFFFF', borderRadius: 3 }}>
                <Pagination
                  count={Math.ceil(filteredData.length / rowsPerPage)}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                  sx={{
                    '& .MuiPaginationItem-root': {
                      borderRadius: 2,
                      '&.Mui-selected': {
                        background: 'linear-gradient(45deg, #1976D2, #2196F3)',
                        color: '#FFFFFF',
                        '&:hover': {
                          background: 'linear-gradient(45deg, #1565C0, #1976D2)',
                        }
                      }
                    }
                  }}
                />
              </Paper>
            </Box>
          </motion.div>
        )}
      </motion.div>
    </Box>
  );
};

export default CaisseTransactions;